// Bulletin.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../utils/firebaseConfig';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useDropzone } from 'react-dropzone';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import heic2any from 'heic2any';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import './Bulletin.css';
/* ──────────────────────────────────────────────
Utility Helpers
────────────────────────────────────────────── */
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const invalidFileTypes = ['video/mp4', 'audio/wav', 'image/gif', 'video/x-ms-wmv'];
function checkFileValidity(file, setError) {
  if (invalidFileTypes.includes(file.type)) {
    setError('Invalid file type. MP4, WAV, GIF, and WMV are not allowed.');
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    setError('File size exceeds 5 MB limit.');
    return false;
  }
  setError('');
  return true;
}
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.5);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}
async function convertImageToJPG(file) {
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    return heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  }
  return compressImage(file);
}
function createImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}
async function getCroppedImg(src, pixelCrop) {
  const img = await createImage(src);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const maxSize = Math.max(img.width, img.height);
  const safe = 2 * ((maxSize / 2) * Math.sqrt(2));
  canvas.width = canvas.height = safe;
  ctx.translate(safe / 2, safe / 2);
  ctx.translate(-safe / 2, -safe / 2);
  ctx.drawImage(img, safe / 2 - img.width * 0.5, safe / 2 - img.height * 0.5);
  const data = ctx.getImageData(0, 0, safe, safe);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(
    data,
    0 - safe / 2 + img.width * 0.5 - pixelCrop.x,
    0 - safe / 2 + img.height * 0.5 - pixelCrop.y
  );
  return new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg'));
}
/* ──────────────────────────────────────────────
FaceDropzone
────────────────────────────────────────────── */
function FaceDropzone({ initialImage = null, onRemove = () => {}, onCroppedImage, label, resetTrigger }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(initialImage);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  
  /* If the parent gives us a new initialImage (e.g. after Firestore fetch),
     update the preview so the picture appears */
  useEffect(() => {
    if (initialImage) {
      setCroppedPreview(initialImage);
    }
  }, [initialImage]);
  
  // Reset the component when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      reset();
    }
  }, [resetTrigger]);
  
  const onCropComplete = useCallback((_, p) => setCroppedAreaPixels(p), []);
  
  const onDrop = async (files) => {
    const file = files[0];
    if (!file) return;
    if (!checkFileValidity(file, setUploadError)) return;
    setIsCropping(true);
    try {
      const blob = await convertImageToJPG(file);
      setSelectedImage(URL.createObjectURL(blob));
      setCroppedPreview(null);
    } catch (err) {
      console.error(err);
      setUploadError('Error processing image.');
    }
  };
  
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop
  });
  
  const saveCrop = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(selectedImage, croppedAreaPixels);
      setCroppedPreview(URL.createObjectURL(blob));
      setSelectedImage(null);
      setIsCropping(false);
      onCroppedImage(blob);
    } catch (err) {
      console.error(err);
      setUploadError('Error cropping image.');
    }
  };
  
  const reset = () => {
    setSelectedImage(null);
    setCroppedPreview(null);
    setIsCropping(false);
    onCroppedImage(null);
  };
  
  return (
    <div className="face-dropzone-wrapper">
      <h4>{label}</h4>
      {!selectedImage && !croppedPreview && (
        <div {...getRootProps()} className="face-dropzone">
          <input {...getInputProps()} />
          <p>Click or Drag/Drop to Upload</p>
        </div>
      )}
      {selectedImage && !croppedPreview && (
        <div className="face-cropper-container">
          <div className="cropper-wrapper">
            <Cropper
              image={selectedImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              style={{ containerStyle: { width: '100%', height: 300 } }}
            />
          </div>
          <div className="cropper-controls">
            <button type="button" onClick={saveCrop} className="btn-save">
              Save
            </button>
            <button type="button" onClick={reset} className="btn-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}
      {croppedPreview && (
        <div className="face-preview-container">
          <img src={croppedPreview} alt="preview" style={{ maxWidth: 200, borderRadius: 4 }} />
          <button
            type="button"
            onClick={() => { reset(); onRemove(); }}
            className="btn-remove"
          >
            ✕
          </button>
        </div>
      )}
      {uploadError && <p className="error-message">{uploadError}</p>}
    </div>
  );
}
/* ──────────────────────────────────────────────
Main Component
────────────────────────────────────────────── */
const Bulletin = () => {
  const { currentUser, isLoggedIn, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Admin editing states
  const [editingFaceQuestion, setEditingFaceQuestion] = useState(null);
  const [editingMultipleChoiceQuestion, setEditingMultipleChoiceQuestion] = useState(null);
  
  // Delete confirmation states
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState({ id: null, type: null });
  
  // Original form state
  const [questionText, setQuestionText] = useState('');
  const [faceBlobs, setFaceBlobs] = useState(Array(6).fill(null));
  const [faceOurPick, setFaceOurPick] = useState(Array(6).fill(false));
  const [faceCorrect, setFaceCorrect] = useState(Array(6).fill(false)); // New state for correct answers
  const [uploading, setUploading] = useState(false);
  
  // ── NEW ── keep track of originals & deletions when editing
  const [originalFaces, setOriginalFaces] = useState(Array(6).fill(null));  // url strings
  const [faceRemoved, setFaceRemoved] = useState(Array(6).fill(false)); // bools
  
  // Reset trigger for form 1
  const [resetForm1Trigger, setResetForm1Trigger] = useState(0);
  
  // New question form state
  const [newQuestionText, setNewQuestionText] = useState('');
  const [questionFaceBlob, setQuestionFaceBlob] = useState(null);
  const [answers, setAnswers] = useState([
    { text: '', isOurPick: false, isCorrect: false },
    { text: '', isOurPick: false, isCorrect: false },
    { text: '', isOurPick: false, isCorrect: false },
    { text: '', isOurPick: false, isCorrect: false }
  ]);
  const [uploadingQuestion, setUploadingQuestion] = useState(false);
  
  // Reset trigger for form 2
  const [resetForm2Trigger, setResetForm2Trigger] = useState(0);
  
  /* ───────────────────── new: pull ?edit=&type= from the URL ────────────────── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    const type = params.get('type');          // 'face' | 'multipleChoice' | null
    
    if (!editId || !type) return;               // nothing to preload
    
    // helper to reset both forms to "clean" before populating
    const clearState = () => {
      setQuestionText('');
      setFaceBlobs(Array(6).fill(null));
      setFaceOurPick(Array(6).fill(false));
      setFaceCorrect(Array(6).fill(false));
      setNewQuestionText('');
      setQuestionFaceBlob(null);
      setAnswers([
        { text:'', isOurPick:false, isCorrect:false },
        { text:'', isOurPick:false, isCorrect:false },
        { text:'', isOurPick:false, isCorrect:false },
        { text:'', isOurPick:false, isCorrect:false }
      ]);
    };
    
    // immediately‑invoked async fn so we can use await
    (async () => {
      try {
        if (type === 'face') {
          const snap = await getDoc(doc(db, 'faceQuestions', editId));
          if (!snap.exists()) throw new Error('Document not found');
          const data = { id: snap.id, ...snap.data() };
          
          clearState();
          
          /* preload basic text and checkbox info — we still require new images */
          setQuestionText(data.questionText || '');
          
          const picksArr = Array(6).fill(false);
          const correctArr = Array(6).fill(false);
          
          data.faces?.forEach((f, idx) => {
            if (f.ourPick) picksArr[idx] = true;
            if (f.isCorrect) correctArr[idx] = true;
          });
          
          setFaceOurPick(picksArr);
          setFaceCorrect(correctArr);
          setEditingFaceQuestion(data);
          
          // Set original faces URLs
          setOriginalFaces(() =>
            Array(6)
              .fill(null)
              .map((_, idx) => data.faces?.[idx]?.imageUrl || null)
          );
          
          // optional: scroll to first form
          document
            .querySelector('.admin-create-question')
            ?.scrollIntoView({ behaviour:'smooth' });
          
        } else if (type === 'multipleChoice') {
          const snap = await getDoc(doc(db, 'questionAnswers', editId));
          if (!snap.exists()) throw new Error('Document not found');
          const data = { id: snap.id, ...snap.data() };
          
          clearState();
          
          setNewQuestionText(data.questionText || '');
          
          /* normalise answers so we always have 4 slots */
          const formatted = data.answers?.map(a => ({
            text: a.text,
            isOurPick: !!a.isOurPick,
            isCorrect: !!a.isCorrect
          })) || [];
          while (formatted.length < 4) formatted.push({ text:'', isOurPick:false, isCorrect:false });
          setAnswers(formatted);
          
          setEditingMultipleChoiceQuestion(data);
          
          // optional: scroll to 2nd form
          document
            .querySelectorAll('.admin-create-question')[1]
            ?.scrollIntoView({ behaviour:'smooth' });
        }
      } catch (err) {
        console.error(err);
        alert('Could not load the selected post for editing.');
        /* fallback: go back to admin without params */
        navigate('/admin', { replace:true });
      }
    })();
  }, [location.search, navigate]);

  /* ─────────  early-exit guard AFTER hooks — ESLint happy  ───────── */
  if (!isLoggedIn || !isAdmin) return <Navigate to="/" replace />;
  
  // replace handleCroppedImage so it also clears "removed" flag
  const handleCroppedImage = (idx, blob) => {
    setFaceBlobs(p => { const a=[...p]; a[idx]=blob; return a; });
    setFaceRemoved(p => { const a=[...p]; a[idx]=false; return a; });
  };
  
  // mark slot as deleted
  const markRemoved = (idx) =>
    setFaceRemoved(p => { const a=[...p]; a[idx]=true; return a; });
  
  const handleQuestionFaceImage = (blob) => {
    setQuestionFaceBlob(blob);
  };
  
  const toggleOurPick = (i) => {
    setFaceOurPick((arr) => arr.map((v, idx) => (idx === i ? !v : false)));
  };
  
  // New function to toggle correct answer
  const toggleCorrect = (i) => {
    setFaceCorrect((arr) => arr.map((v, idx) => (idx === i ? !v : false)));
  };
  
  const handleAnswerChange = (index, field, value) => {
    setAnswers(prev => {
      const newAnswers = [...prev];
      if (field === 'isOurPick' && value === true) {
        // If setting a new "Our Pick", unset any existing ones
        newAnswers.forEach(answer => answer.isOurPick = false);
      }
      if (field === 'isCorrect' && value === true) {
        // If setting a new "Correct Answer", unset any existing ones
        newAnswers.forEach(answer => answer.isCorrect = false);
      }
      newAnswers[index] = { ...newAnswers[index], [field]: value };
      return newAnswers;
    });
  };
  
  // Confirmation dialog for deletion
  const DeleteConfirmationDialog = ({ onConfirm, onCancel }) => {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h3>Confirm Deletion</h3>
          <p>Are you sure you want to delete this question? This action cannot be undone.</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={onCancel}
              style={{
                background: '#9e9e9e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              style={{
                background: '#F44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Function to confirm deletion
  const confirmDelete = async () => {
    const { id, type } = questionToDelete;
    try {
      if (type === 'face') {
        // Delete the face question and its votes
        await deleteDoc(doc(db, 'faceQuestions', id));
        // Delete all votes for this question
        const votesSnapshot = await getDocs(collection(db, 'faceQuestions', id, 'votes'));
        const deletePromises = votesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      } else if (type === 'multipleChoice') {
        // Delete the multiple choice question and its votes
        await deleteDoc(doc(db, 'questionAnswers', id));
        // Delete all votes for this question
        const votesSnapshot = await getDocs(collection(db, 'questionAnswers', id, 'votes'));
        const deletePromises = votesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
      }
      // Close the confirmation dialog
      setShowDeleteConfirmation(false);
      setQuestionToDelete({ id: null, type: null });
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question. Please try again.');
    }
  };
  
  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!questionText.trim()) {
      return alert('Please provide a question.');
    }
    
    // Build the face data array
    const faceData = [];
    
    // Process each face slot
    for (let i = 0; i < 6; i++) {
      if (faceRemoved[i]) continue; // Skip deleted slots
      
      if (faceBlobs[i]) {
        // New upload - compress → storage → push
        const path = `faceQuestions/${Date.now()}_face${i}.jpg`;
        const sRef = ref(storage, path);
        try {
          setUploading(true);
          await uploadBytes(sRef, faceBlobs[i]);
          faceData.push({
            imageUrl: await getDownloadURL(sRef),
            ourPick: faceOurPick[i],
            isCorrect: faceCorrect[i]
          });
        } catch (err) {
          console.error(err);
          alert('Error uploading image.');
          setUploading(false);
          return;
        }
      } else if (editingFaceQuestion && originalFaces[i]) {
        // Keep original URL
        faceData.push({
          imageUrl: originalFaces[i],
          ourPick: faceOurPick[i],
          isCorrect: faceCorrect[i]
        });
      }
    }
    
    // Validate we have at least 2 faces
    if (faceData.length < 2) {
      return alert('Please provide at least 2 face images.');
    }
    
    try {
      if (editingFaceQuestion) {
        // Update existing question
        await setDoc(doc(db, 'faceQuestions', editingFaceQuestion.id), {
          questionText: questionText.trim(),
          faces: faceData,
          // Keep the original timestamp
          createdAt: editingFaceQuestion.createdAt,
          // Add last edited timestamp
          lastEdited: serverTimestamp()
        });
        alert('Question updated!');
        setEditingFaceQuestion(null);
      } else {
        // Create new question
        await addDoc(collection(db, 'faceQuestions'), {
          questionText: questionText.trim(),
          faces: faceData,
          createdAt: serverTimestamp()
        });
        alert('Question created!');
      }
      // Reset form
      setQuestionText('');
      setFaceBlobs(Array(6).fill(null));
      setFaceOurPick(Array(6).fill(false));
      setFaceCorrect(Array(6).fill(false));
      setOriginalFaces(Array(6).fill(null));
      setFaceRemoved(Array(6).fill(false));
      setResetForm1Trigger(prev => prev + 1);
    } catch (err) {
      console.error(err);
      alert('Error ' + (editingFaceQuestion ? 'updating' : 'creating') + ' question.');
    } finally {
      setUploading(false);
    }
  };
  
  const handleCreateNewQuestion = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    // Validation
    if (!newQuestionText.trim()) {
      return alert('Please provide a question text.');
    }
    if (!questionFaceBlob && !editingMultipleChoiceQuestion) {
      return alert('Please upload a face image.');
    }
    // Check if at least two answers have text
    const validAnswers = answers.filter(answer => answer.text.trim() !== '');
    if (validAnswers.length < 2) {
      return alert('Please provide at least 2 answers.');
    }
    try {
      setUploadingQuestion(true);
      let imageUrl = null;
      // If we're editing and no new image was uploaded, use the existing image
      if (editingMultipleChoiceQuestion && !questionFaceBlob) {
        imageUrl = editingMultipleChoiceQuestion.imageUrl;
      }
      // Otherwise, upload the new image
      else if (questionFaceBlob) {
        const path = `questionAnswers/${Date.now()}_question.jpg`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, questionFaceBlob);
        imageUrl = await getDownloadURL(sRef);
      }
      // Filter out empty answers
      const filteredAnswers = answers
        .filter(answer => answer.text.trim() !== '')
        .map(answer => ({
          text: answer.text.trim(),
          isOurPick: answer.isOurPick,
          isCorrect: answer.isCorrect,
          votes: 0
        }));
      if (editingMultipleChoiceQuestion) {
        // Update existing question
        await setDoc(doc(db, 'questionAnswers', editingMultipleChoiceQuestion.id), {
          questionText: newQuestionText.trim(),
          imageUrl: imageUrl,
          answers: filteredAnswers,
          // Keep the original timestamp
          createdAt: editingMultipleChoiceQuestion.createdAt,
          // Add last edited timestamp
          lastEdited: serverTimestamp(),
          type: 'multipleChoice'
        });
        alert('Question updated!');
        setEditingMultipleChoiceQuestion(null);
      } else {
        // Create new question
        await addDoc(collection(db, 'questionAnswers'), {
          questionText: newQuestionText.trim(),
          imageUrl: imageUrl,
          answers: filteredAnswers,
          createdAt: serverTimestamp(),
          type: 'multipleChoice'
        });
        alert('New question created!');
      }
      // Reset form
      setNewQuestionText('');
      setQuestionFaceBlob(null);
      setAnswers([
        { text: '', isOurPick: false, isCorrect: false },
        { text: '', isOurPick: false, isCorrect: false },
        { text: '', isOurPick: false, isCorrect: false },
        { text: '', isOurPick: false, isCorrect: false }
      ]);
      setResetForm2Trigger(prev => prev + 1);
    } catch (err) {
      console.error(err);
      alert('Error ' + (editingMultipleChoiceQuestion ? 'updating' : 'creating') + ' question: ' + err.message);
    } finally {
      setUploadingQuestion(false);
    }
  };
  
  // Styles for the new question form
  const answerBoxStyle = {
    marginBottom: '15px',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    backgroundColor: '#f9f9f9'
  };
  const checkboxContainerStyle = {
    display: 'flex',
    gap: '20px',
    marginTop: '10px'
  };
  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer'
  };
  
  /* ──────────────────────────  JSX  ────────────────────────── */
  return (
    <div className="face-voting-container">
      {/* ───── Original Admin Form ───── */}
      {isAdmin && (
        <div
          className="admin-create-question"
          style={{
            maxWidth: '900px',
            padding: '30px',
            margin: '30px auto'
          }}
        >
          <h2>{editingFaceQuestion ? 'Edit Face Question' : 'Create New Question (Admin Only)'}</h2>
          <form onSubmit={handleCreateQuestion}>
            <label style={{ fontWeight: 'bold' }}>Question Text</label>
            <textarea
              rows={3}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            <p>Upload up to 6 faces (at least 2 required)</p>
            <div
              className="faces-dropzones-container"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                alignItems: 'center'
              }}
            >
              {faceBlobs.map((_, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <FaceDropzone
                    key={`${editingFaceQuestion?.id || 'new'}-${i}`}
                    label={`Face #${i + 1}`}
                    initialImage={!faceRemoved[i]
                      ? faceBlobs[i]
                        ? URL.createObjectURL(faceBlobs[i])
                        : originalFaces[i]
                      : null}
                    onCroppedImage={(blob) => handleCroppedImage(i, blob)}
                    onRemove={() => markRemoved(i)}
                    resetTrigger={resetForm1Trigger}
                  />
                  <div style={checkboxContainerStyle}>
                    <label
                      style={checkboxLabelStyle}
                    >
                      <input
                        type="checkbox"
                        checked={faceOurPick[i]}
                        onChange={() => toggleOurPick(i)}
                        disabled={faceRemoved[i] && !faceBlobs[i] && !originalFaces[i]}
                      />
                      Our Pick
                    </label>
                    <label
                      style={checkboxLabelStyle}
                    >
                      <input
                        type="checkbox"
                        checked={faceCorrect[i]}
                        onChange={() => toggleCorrect(i)}
                        disabled={faceRemoved[i] && !faceBlobs[i] && !originalFaces[i]}
                      />
                      Correct
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="submit"
              disabled={uploading}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading
                ? 'Uploading...'
                : editingFaceQuestion
                  ? 'Update Question'
                  : 'Create Question'}
            </button>
          </form>
        </div>
      )}
            {/* ───── New Multiple Choice Question Form ───── */}
            {isAdmin && (
        <div
          className="admin-create-question"
          style={{
            maxWidth: '900px',
            padding: '30px',
            margin: '50px auto'
          }}
        >
          <h2>{editingMultipleChoiceQuestion ? 'Edit Multiple Choice Question' : 'Create Multiple Choice Question'}</h2>
          <form onSubmit={handleCreateNewQuestion}>
            <label style={{ fontWeight: 'bold' }}>Question Text</label>
            <textarea
              rows={3}
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            <div style={{ marginBottom: '20px' }}>
              <FaceDropzone
                key={`${editingMultipleChoiceQuestion?.id || 'new'}-question`}
                label="Upload Question Image"
                initialImage={editingMultipleChoiceQuestion?.imageUrl || null}
                onCroppedImage={handleQuestionFaceImage}
                resetTrigger={resetForm2Trigger}
              />
            </div>
            <label style={{ fontWeight: 'bold' }}>Answer Options</label>
            {answers.map((answer, index) => (
              <div key={index} style={answerBoxStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '30px' }}>{index + 1}.</span>
                  <input
                    type="text"
                    value={answer.text}
                    onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                    placeholder={`Answer option ${index + 1}`}
                    style={{ flex: 1, padding: '8px' }}
                  />
                </div>
                <div style={checkboxContainerStyle}>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={answer.isOurPick}
                      onChange={() => handleAnswerChange(index, 'isOurPick', !answer.isOurPick)}
                    />
                    Our Pick
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={answer.isCorrect}
                      onChange={() => handleAnswerChange(index, 'isCorrect', !answer.isCorrect)}
                    />
                    Correct
                  </label>
                </div>
              </div>
            ))}
            <button
              type="submit"
              disabled={uploadingQuestion}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: uploadingQuestion ? 'not-allowed' : 'pointer'
              }}
            >
              {uploadingQuestion
                ? 'Uploading...'
                : editingMultipleChoiceQuestion
                  ? 'Update Question'
                  : 'Create Question'}
            </button>
          </form>
        </div>
      )}
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <DeleteConfirmationDialog
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteConfirmation(false);
            setQuestionToDelete({ id: null, type: null });
          }}
        />
      )}
    </div>
  );
};

export default Bulletin;